import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { RequestFormError, RequestFormErrorForm, RequestFormErrorSearchForm } from 'entities/request_form_error'

type RequestFormErrorsResponse = PagingResponse & {
    results: RequestFormError[]
}

export function useFetchRequestFormErrorsApi(searchForm?: Form<RequestFormErrorSearchForm>): IndexApiSet<RequestFormErrorsResponse> & { execute: () => void } {
    const apiPath = 'request_form_errors/'
    const api = useIndexApi<RequestFormErrorsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvRequestFormErrorsApi(searchForm: Form<RequestFormErrorSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'request_form_errors/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'request_form_error', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostRequestFormErrorApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormErrorForm>) => void } {
    const apiSet = usePostApi<BaseResponse, RequestFormErrorForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<RequestFormErrorForm>) => {
        const apiPath = `request_form_errors/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutRequestFormErrorApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormErrorForm>) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormErrorForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<RequestFormErrorForm>) => {
        const apiPath = `request_form_errors/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutRequestFormErrorsApi(): ApiSet<BaseResponse> & { execute: (form: Form<RequestFormErrorForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormErrorForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<RequestFormErrorForm[]>) => {
        const apiPath = `request_form_errors/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteRequestFormErrorApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `request_form_errors/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useConfirmRequestFormErrorApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = usePutApi<BaseResponse, RequestFormErrorForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (id: number) => {
        const apiPath = `request_form_errors/${id}/confirm/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}
